/**
 * React hooks for the shared SDK. Kept in a separate entry point so the core crypto module
 * stays framework-free for Workers and Node.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { AccountRole } from "./crypto.ts";
import {
  AUTH_STORAGE_KEY,
  clearAuthSession,
  readAuthSession,
  writeAuthSession,
} from "./session.ts";

export interface TensileAuthState {
  token: string | null;
  email: string | null;
  accountId: string | null;
  role: AccountRole | null;
  authenticated: boolean;
  setSession: (session: { token: string; email?: string; accountId?: string; role?: AccountRole }) => void;
  signOut: () => void;
}

/** Reads the current TensileAuth session and keeps it in sync across tabs. */
export function useTensileAuth(): TensileAuthState {
  const [session, setSessionState] = useState(() => readSession());

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === AUTH_STORAGE_KEY) setSessionState(readSession());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setSession = useCallback<TensileAuthState["setSession"]>((next) => {
    const value = {
      token: next.token,
      email: next.email ?? null,
      accountId: next.accountId ?? null,
      role: next.role ?? null,
    };
    writeAuthSession(value);
    setSessionState(value);
  }, []);

  const signOut = useCallback(() => {
    clearAuthSession();
    if (typeof sessionStorage !== "undefined") {
      for (const key of Object.keys(sessionStorage)) {
        if (key.startsWith("tensile.vault.key.")) sessionStorage.removeItem(key);
      }
    }
    setSessionState({ token: null, email: null, accountId: null, role: null });
  }, []);

  return useMemo(
    () => ({
      ...session,
      authenticated: Boolean(session.token),
      setSession,
      signOut,
    }),
    [session, setSession, signOut],
  );
}

interface StoredSession {
  token: string | null;
  email: string | null;
  accountId: string | null;
  role: AccountRole | null;
}

function readSession(): StoredSession {
  return readAuthSession() ?? { token: null, email: null, accountId: null, role: null };
}

export interface AppVaultState {
  appId: string;
  unlocked: boolean;
  unlock: (appScopedSecretKeyB64: string) => void;
  lock: () => void;
  /** Short-lived, tab-scoped; never persisted to localStorage. */
  appScopedSecretKeyB64: string | null;
}

export function useAppVault(appId: string): AppVaultState {
  const storageKey = `tensile.vault.key.${appId}`;
  const [key, setKey] = useState<string | null>(() =>
    typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(storageKey),
  );

  const unlock = useCallback(
    (appScopedSecretKeyB64: string) => {
      sessionStorage.setItem(storageKey, appScopedSecretKeyB64);
      setKey(appScopedSecretKeyB64);
    },
    [storageKey],
  );

  const lock = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setKey(null);
  }, [storageKey]);

  return {
    appId,
    unlocked: Boolean(key),
    unlock,
    lock,
    appScopedSecretKeyB64: key,
  };
}
